/**
 * Position Sizing MC Mock
 *
 * Shows how Monte Carlo drawdown informs position scaling.
 * Compares backtest-based sizing vs MC-based sizing with
 * a visual scaling factor calculation.
 */

const scenarios = [
  {
    label: 'Backtest-based sizing',
    dd: 18,
    tolerance: 25,
    scale: 1.0,
    verdict: 'Full size',
    verdictColor: '#22c55e',
    barColor: '#3b82f6',
    notes: 'Uses single backtest DD',
  },
  {
    label: 'MC P95-based sizing',
    dd: 42,
    tolerance: 25,
    scale: 0.60,
    verdict: '60% size',
    verdictColor: '#f59e0b',
    barColor: '#ef4444',
    notes: 'Uses 95th percentile DD',
  },
];

const maxDD = 50;

export default function PositionSizingMCMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Position Sizing from Monte Carlo
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem 1rem' }}>
        {/* Tolerance marker */}
        <div style={{
          fontSize: '0.5625rem', color: '#64748b', marginBottom: '0.625rem',
          textAlign: 'center',
        }}>
          Maximum drawdown tolerance: <strong style={{ color: '#e5e7eb' }}>-25%</strong>
        </div>

        {scenarios.map((s) => (
          <div key={s.label} style={{
            marginBottom: '0.75rem',
            background: '#1e293b',
            borderRadius: 4,
            padding: '0.5rem',
            border: '1px solid #334155',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
              <span style={{ fontSize: '0.5625rem', color: '#94a3b8' }}>{s.label}</span>
              <span style={{ fontSize: '0.5rem', color: '#475569' }}>{s.notes}</span>
            </div>

            {/* Drawdown bar */}
            <div style={{ position: 'relative', height: 18, marginBottom: '0.3rem' }}>
              <div style={{
                height: '100%', background: '#0f172a', borderRadius: 3, overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${(s.dd / maxDD) * 100}%`,
                  background: `${s.barColor}22`,
                  border: `1px solid ${s.barColor}44`,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '0.375rem',
                }}>
                  <span style={{
                    fontSize: '0.5625rem', fontWeight: 700, color: s.barColor,
                    fontFamily: 'ui-monospace, monospace',
                  }}>
                    -{s.dd}%
                  </span>
                </div>
              </div>
              {/* Tolerance line */}
              <div style={{
                position: 'absolute',
                left: `${(25 / maxDD) * 100}%`,
                top: -2, bottom: -2,
                borderLeft: '1.5px dashed #f59e0b',
              }} />
            </div>

            {/* Result row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.5rem', color: '#64748b', fontFamily: 'ui-monospace, monospace' }}>
                Scale = {s.tolerance}% / {s.dd}% = <strong style={{ color: '#e5e7eb' }}>{s.scale.toFixed(2)}</strong>
              </span>
              <span style={{
                fontSize: '0.5625rem', fontWeight: 700, color: s.verdictColor,
                background: `${s.verdictColor}15`,
                padding: '0.1rem 0.4rem',
                borderRadius: 3,
              }}>
                {s.verdict}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="platform-mock-caption">
        MC reveals the backtest sizing is too aggressive. Scale to 60% to stay within tolerance.
      </div>
    </div>
  );
}
